import React, { useEffect, useState } from "react";
import { Tablet } from "lucide-react";
import "../style/deviceList.css";
import axios from "axios";
import { useAuth } from "../context/authContext";
import Button from "../components/Button";
import { debounce } from "lodash";
import config from '../config';

const DeviceModal = ({
  showModal,
  handleCloseModal,
  modalSearch,
  setModalSearch,
  selectedDevice,
  handleDeviceSelection,
  handleSave,
  loading,
  organizationId,
}) => {
  const { authData } = useAuth();
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Debounced search function
  const debouncedSearch = React.useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm) {
        setFilteredDevices([]);
        return;
      }

      setSearchLoading(true);
      setSearchError(null);
      
      try {
        const response = await axios.get(
          `${config.BASE_URL}/devices/search/${encodeURIComponent(searchTerm)}`,
          {
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        setFilteredDevices(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error searching devices:", error);
        setSearchError("Failed to search devices. Please try again.");
        setFilteredDevices([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    [authData.accessToken]
  );

  // Trigger search when modalSearch changes
  useEffect(() => {
    if (modalSearch) {
      debouncedSearch(modalSearch.trim());
    } else {
      setFilteredDevices([]);
    }

    return () => debouncedSearch.cancel();
  }, [modalSearch, debouncedSearch]);

  // Clear search when modal closes
  const handleClose = () => {
    setModalSearch("");
    setFilteredDevices([]);
    setSearchError(null);
    handleCloseModal();
  };

  const handleSaveAndClose = () => {
   handleSave(selectedDevice)
    setModalSearch("");
    setFilteredDevices([]);
  };

  if (!showModal) return null;

  return (
    <div className="deviceList-modal">
      <div className="deviceList-modalContent">
        <div className="deviceList-modalHeader">
          <h2>Associate Device</h2>
          <button className="deviceList-closeButton" onClick={handleClose}>
            ×
          </button>
        </div>
        <div className="deviceList-modalBody">
          <input
            type="text"
            className="deviceList-modalSearchInput"
            placeholder="Search by device ID, name..."
            value={modalSearch}
            onChange={(e) => setModalSearch(e.target.value)}
            autoFocus
          />
          
        
          <div className="deviceList-list">
            {searchLoading ? (
              <div className="deviceList-loading">Searching devices...</div>
            ) : searchError ? (
              <div className="deviceList-error">{searchError}</div>
            ) : filteredDevices.length > 0 ? (
              filteredDevices.map((device) => (
                <div
                  className={`deviceList-item ${
                    selectedDevice?.id === device.id ? "selected" : ""
                  }`}
                  key={device.id}
                  onClick={() => handleDeviceSelection(device)}
                >
                  <Tablet />
                  <div className="deviceList-info">
                    <strong>
                      {device.device?.deviceName || "Unnamed Device"}
                    </strong>
                    <div className="deviceList-details">
                      <small>ID: {device.id}</small>
                      {device.device?.model && (
                        <small>Model: {device.device.model}</small>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : modalSearch ? (
              <div className="deviceList-noResults">
                No devices found matching "{modalSearch}"
              </div>
            ) : null}
          </div>
        </div>
        <div className="deviceList-modalFooter">
          <Button
            className="deviceList-saveButton"
            onClick={handleSaveAndClose}
            disabled={!selectedDevice || loading}
            text={loading ? "Saving..." : "Save"}
            color="#006d7d"
          />
        </div>
      </div>
    </div>
  );
};

export default DeviceModal;